import { Button, Flexbox, Select, Text, Spinner } from '@components/common'
import React, { useState } from 'react'
import { ImportColumn, ImportType } from './importTypes'
import { getOptions } from './columns'

interface ImportMapProps {
  file: File
  entity: ImportType
  updateMapping: (mapping: { [key: string]: ImportColumn }) => void
  onLoadingChange?: (loading: boolean) => void
  options?: ImportColumn[]
  isOptionsLoading?: boolean
}

const ImportMap: React.FC<ImportMapProps> = ({
  file,
  entity,
  updateMapping,
  onLoadingChange,
  options: optionsProp,
  isOptionsLoading = false,
}) => {
  const [headers, setHeaders] = useState<string[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [mapping, setMapping] = useState<{ [key: string]: ImportColumn }>({})
  const [isLoadingHeaders, setIsLoadingHeaders] = useState(false)

  const options = optionsProp ?? getOptions(entity)

  // this is used to notify parent component when loading state changes
  React.useEffect(() => {
    onLoadingChange?.(isLoadingHeaders)
  }, [isLoadingHeaders, onLoadingChange])

  React.useEffect(() => {
    // Read the first line of the file to get the headers
    if (file) {
      setIsLoadingHeaders(true)
      setHeaders([])
      setSelected([])
      setMapping({})

      const reader = new FileReader()
      reader.onload = e => {
        try {
          const lines = (e.target.result as string).split('\n')
          const headerLine = lines[0].split(',').map(h => h.replace('\r', '').trim())
          setHeaders(headerLine)

          const newMapping: { [key: string]: ImportColumn } = {}
          const newSelected: string[] = []

          headerLine.forEach(header => {
            const find = options.find(o => o.label === header)
            if (find) {
              newMapping[header] = find
              newSelected.push(find.value)
            }
          })

          setMapping(newMapping)
          setSelected(newSelected)
        } catch (error) {
          console.error('Error reading file headers:', error)
        } finally {
          setIsLoadingHeaders(false)
        }
      }

      reader.onerror = () => {
        console.error('Error reading file')
        setIsLoadingHeaders(false)
      }

      reader.readAsText(file)
    } else {
      setHeaders([])
      setSelected([])
      setMapping({})
      setIsLoadingHeaders(false)
    }
  }, [file, options])

  React.useEffect(() => {
    updateMapping(mapping)
  }, [mapping, updateMapping])

  const handleMappingChange = React.useCallback((e: ImportColumn, header: string) => {
    const previousValue = mapping[header]?.value
    setSelected(s => {
      const without = previousValue ? s.filter(v => v !== previousValue) : s
      return [...without, e.value]
    })
    setMapping(m => ({
      ...m,
      [header]: e,
    }))
  }, [mapping])

  const clearItem = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const field = e.currentTarget.id

    if (field) {
      const mappedValue = mapping[field]?.value
      setSelected(s => s.filter(v => v !== mappedValue))
      setMapping(m => ({
        ...m,
        [field]: null,
      }))
    }
  }

  if (!file) return <></>

  if (isOptionsLoading) {
    return (
      <Flexbox
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={12}
        style={{ padding: '32px 16px' }}
      >
        <Spinner size="regular" />
        <Text style={{ fontStyle: 'italic' }}>Loading field labels...</Text>
      </Flexbox>
    )
  }

  if (isLoadingHeaders) {
    return (
      <Flexbox
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={12}
        style={{ padding: '32px 16px' }}
      >
        <Spinner size="regular" />
        <Text style={{ fontStyle: 'italic' }}>Reading file and mapping columns...</Text>
      </Flexbox>
    )
  }

  if (headers.length === 0) {
    return (
      <Flexbox
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        style={{ padding: '32px 16px' }}
      >
        <Text style={{ fontStyle: 'italic', color: '#666' }}>
          No headers found in the file
        </Text>
      </Flexbox>
    )
  }

  return (
    <Flexbox flexDirection="column" gap={12} style={{ padding: '16px' }}>
      <Flexbox flexDirection="row" alignItems="center" gap={12}>
        <Text style={{ width: '30%', fontSize: '1.1rem' }}>File Column</Text>
        <Text style={{ flexGrow: 1, fontSize: '1.1rem' }}>Map to</Text>
      </Flexbox>
      {headers.map(header => (
        <Flexbox
          flexDirection="row"
          alignItems="center"
          key={header}
          gap={12}
          style={{ borderBottom: '1px solid whitesmoke', padding: '8px 0' }}
        >
          <Text bold style={{ width: '30%' }}>
            {header}
          </Text>
          <Select
            placeholder="Unmapped"
            flexGrow
            onChange={e => handleMappingChange(e, header)}
            value={mapping[header]}
            options={options.filter(o => !selected.includes(o.value))}
          />
          <Button
            shape="pill"
            appearance="muted"
            intent="danger"
            size="compact"
            id={header}
            onClick={clearItem}
          >
            Clear
          </Button>
        </Flexbox>
      ))}
    </Flexbox>
  )
}

export default ImportMap
